import { useCallback, useEffect, useRef, useState } from "react";
import { Link2, Sparkles, AlertCircle, RotateCcw, Youtube, Music2, Instagram, Facebook, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { analyzeUrl } from "@/lib/api";
import AnalysisProgress from "@/components/AnalysisProgress";
import ResultCard from "@/components/ResultCard";

const PLATFORMS = [
  { Icon: Youtube, name: "YouTube", host: "youtube" },
  { Icon: Music2, name: "TikTok", host: "tiktok" },
  { Icon: Instagram, name: "Instagram", host: "instagram" },
  { Icon: Facebook, name: "Facebook", host: "facebook" },
  { Icon: Linkedin, name: "LinkedIn", host: "linkedin" },
];

const URL_PATTERN =
  /^https?:\/\/(www\.|m\.|vm\.)?(youtube\.com|youtu\.be|tiktok\.com|instagram\.com|facebook\.com|fb\.watch|linkedin\.com)\//i;

export default function UrlAnalyzer({ compact = false }) {
  const [url, setUrl] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | analyzing | done | error
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const inputRef = useRef(null);
  const resultRef = useRef(null);

  useEffect(() => {
    if (phase === "done" && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [phase]);

  const reset = useCallback(() => {
    setUrl("");
    setPhase("idle");
    setResult(null);
    setErrorMsg("");
    inputRef.current?.focus();
  }, []);

  const submit = async (e) => {
    if (e) e.preventDefault();
    const trimmed = url.trim();
    if (!trimmed) {
      toast.error("Pega una URL de video");
      return;
    }
    if (!URL_PATTERN.test(trimmed)) {
      toast.error("URL no válida. Usa YouTube, TikTok, Instagram, Facebook o LinkedIn.");
      return;
    }

    setPhase("analyzing");
    setErrorMsg("");
    setResult(null);

    try {
      const data = await analyzeUrl(trimmed);
      setResult(data);
      setPhase("done");
      toast.success("Análisis completado");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Error desconocido al analizar el video";
      setErrorMsg(msg);
      setPhase("error");
      toast.error(msg);
    }
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) setUrl(text.trim());
      inputRef.current?.focus();
    } catch {
      toast.error("No se pudo acceder al portapapeles");
    }
  };

  const busy = phase === "analyzing";

  return (
    <div className="w-full" data-testid="url-analyzer">
      {phase !== "done" && (
        <form
          onSubmit={submit}
          noValidate
          className={`relative ${compact ? "" : "vx-fade-up"}`}
        >
          <div className="vx-card rounded-2xl p-1.5 backdrop-blur-sm">
            <div className="flex items-center gap-2 px-3">
              <Link2 className="w-5 h-5 text-[--vx-text-faint] shrink-0" />
              <Input
                ref={inputRef}
                data-testid="url-input"
                type="text"
                inputMode="url"
                placeholder="Pega la URL del video aquí…"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={busy}
                autoComplete="off"
                className="flex-1 bg-transparent border-0 h-14 text-base focus-visible:ring-0 focus-visible:ring-offset-0 px-0 placeholder:text-[--vx-text-faint] text-white"
              />
              <button
                type="button"
                onClick={handlePaste}
                disabled={busy}
                className="hidden sm:inline-flex items-center text-xs font-mono uppercase tracking-wider text-[--vx-text-dim] hover:text-white px-3 py-1.5 rounded-lg border border-[--vx-border-strong] hover:border-[--vx-text-dim] transition-colors disabled:opacity-50"
                data-testid="paste-btn"
              >
                Pegar
              </button>
              <Button
                type="submit"
                disabled={busy || !url.trim()}
                data-testid="analyze-btn"
                className="h-12 px-5 sm:px-6 rounded-xl bg-white text-black hover:bg-white/90 font-semibold disabled:opacity-50"
              >
                {busy ? (
                  <>
                    <span className="w-4 h-4 rounded-full border-2 border-black/30 border-t-black vx-spin mr-2" />
                    Analizando
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" strokeWidth={2.5} />
                    Analizar
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Platform chips */}
          <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[--vx-text-faint] mr-1">
              Compatible con
            </span>
            {PLATFORMS.map(({ Icon, name }) => (
              <span
                key={name}
                className="inline-flex items-center gap-1.5 text-xs text-[--vx-text-dim] px-2.5 py-1 rounded-full border border-[--vx-border] bg-[--vx-surface]/60"
                data-testid={`platform-${name.toLowerCase()}`}
              >
                <Icon className="w-3.5 h-3.5" strokeWidth={2} />
                {name}
              </span>
            ))}
          </div>
        </form>
      )}

      {phase === "analyzing" && (
        <div className="mt-6">
          <AnalysisProgress />
        </div>
      )}

      {phase === "error" && (
        <div
          className="mt-6 vx-card rounded-2xl p-5 flex items-start gap-3"
          data-testid="error-msg"
        >
          <div className="shrink-0 w-10 h-10 rounded-full bg-[--vx-fake]/10 grid place-items-center">
            <AlertCircle className="w-5 h-5 text-[--vx-fake]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-base">
              No se pudo analizar el video
            </div>
            <p className="text-sm text-[--vx-text-dim] mt-1">{errorMsg}</p>
            <Button
              onClick={() => {
                setPhase("idle");
                setErrorMsg("");
              }}
              className="mt-4 rounded-xl bg-white text-black hover:bg-white/90 font-semibold"
              data-testid="retry-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Probar de nuevo
            </Button>
          </div>
        </div>
      )}

      {phase === "done" && result && (
        <div ref={resultRef} className="mt-6">
          <ResultCard result={result} onReset={reset} compact={compact} />
        </div>
      )}
    </div>
  );
}
